import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "./utility-agent-form.module.scss";

type UtilityAgentFormProps = {
  parsedContent: any;
  messageId?: string;
};

const UTILITY_FORM_ENDPOINT = "/stream/coc-review";

function UtilityFormComposer({
  onSendMessage,
}: {
  onSendMessage: (msg: string) => void;
}) {
  const [buyerInfo, setBuyerInfo] = useState("");
  const [sellerInfo, setSellerInfo] = useState("");
  const [percentageStake, setPercentageStake] = useState("");
  const [transactionType, setTransactionType] = useState("");

  const isComplete =
    buyerInfo.trim() &&
    sellerInfo.trim() &&
    percentageStake.trim() &&
    transactionType.trim();

  const handleSubmit = () => {
    if (!isComplete) return;

    const parts = [
      `Buyer Information: ${buyerInfo.trim()}`,
      `Seller Information: ${sellerInfo.trim()}`,
      `Percentage Stake: ${percentageStake.trim()}`,
      `Transaction Type: ${transactionType.trim()}`,
    ];

    onSendMessage(parts.join("\n\n"));
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      fontSize: "14px",
      "& fieldset": { borderColor: "#d0e8e4" },
      "&:hover fieldset": { borderColor: "#00a888" },
      "&.Mui-focused fieldset": { borderColor: "#00a888" },
    },
  };

  const questionSx = {
    fontWeight: 600,
    fontSize: "13px",
    color: "#004141",
    mb: 1,
  };

  return (
    <Box className={styles.intakeComposer}>
      {/* Header */}
      <Box className={styles.intakeComposerHeader}>
        <Box>
          <Typography className={styles.intakeComposerEyebrow}>
            Transaction Details
          </Typography>
          <Typography className={styles.intakeComposerTitle}>
            Let's get the details
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.5 }}>
            Fill in the fields below to proceed with the transaction analysis.
          </Typography>
        </Box>
      </Box>

      {/* Fields */}
      <Box sx={{ px: "20px", pt: "16px" }}>
        <Box mb={2.5}>
          <Typography sx={questionSx}>
            Buyer Information
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "#6b7280", mb: 1, lineHeight: 1.4 }}
          >
            Provide details about the buyer (company name)
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={buyerInfo}
            onChange={(e) => setBuyerInfo(e.target.value)}
            placeholder="e.g., Acme Corporation LLC"
            sx={fieldSx}
          />
        </Box>

        <Box mb={2.5}>
          <Typography sx={questionSx}>
            Seller Information
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "#6b7280", mb: 1, lineHeight: 1.4 }}
          >
            Provide details about the seller
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={sellerInfo}
            onChange={(e) => setSellerInfo(e.target.value)}
            placeholder="e.g., TechVentures Inc."
            sx={fieldSx}
          />
        </Box>

        <Box mb={2.5}>
          <Typography sx={questionSx}>
            Percentage Stake or Ownership
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "#6b7280", mb: 1, lineHeight: 1.4 }}
          >
            What percentage of ownership is being acquired?
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={percentageStake}
            onChange={(e) => setPercentageStake(e.target.value)}
            placeholder="e.g., 51%, 100%, or specific percentage"
            sx={fieldSx}
          />
        </Box>

        <Box mb={2}>
          <Typography sx={questionSx}>
            Transaction Type
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "#6b7280", mb: 1, lineHeight: 1.4 }}
          >
            What type of transaction is this?
          </Typography>
          <Select
            fullWidth
            size="small"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: "#fff",
              fontSize: "14px",
              "& fieldset": { borderColor: "#d0e8e4" },
              "&:hover fieldset": { borderColor: "#00a888" },
              "&.Mui-focused fieldset": { borderColor: "#00a888" },
            }}
          >
            <MenuItem value="" disabled>
              <span style={{ color: "#9ca3af" }}>Select transaction type</span>
            </MenuItem>
            <MenuItem value="Share Transaction">Share Transaction</MenuItem>
            <MenuItem value="Assert Transaction">Asset Transaction</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={styles.intakeComposerFooter}>
        {!isComplete && (
          <Typography sx={{ fontSize: "12px", color: "#9ca3af" }}>
            Fill in all fields to continue
          </Typography>
        )}
        <Button
          variant="contained"
          size="small"
          disabled={!isComplete}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": { backgroundColor: "#00947a" },
            "&.Mui-disabled": { backgroundColor: "#b2e5de", color: "#fff" },
          }}
        >
          Continue →
        </Button>
      </Box>
    </Box>
  );
}

export default function UtilityAgentForm({
  parsedContent,
  messageId,
}: UtilityAgentFormProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);

  const data = parsedContent?.finalAnswer || parsedContent;
  const question = data?.question || parsedContent?.question || "";

  // Check if this is the latest message to show the form
  const latestAgentMessage = [...currentChat]
    .reverse()
    .find((message) => message.sender === "agent");
  const showForm = latestAgentMessage?.id === messageId;

  const handleSendMessage = (msg: string) => {
    if (!currentAgent?.id) return;

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent.id,
        message: msg,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: UTILITY_FORM_ENDPOINT,
        sessionId: null,
      }),
    );
  };

  return (
    <Box className={styles.conversationContainer}>
      {/* Question/Prompt - always visible */}
      {question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionIcon}>
            <InfoOutlinedIcon sx={{ color: "#6b7280", fontSize: 18 }} />
          </Box>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={question} />
          </Box>
        </Box>
      )}

      {/* Form - only shows for the latest message */}
      {showForm && <UtilityFormComposer onSendMessage={handleSendMessage} />}
    </Box>
  );
}
